/**
 * Cart.js - AJAX Cart Functionality for Shopify
 * Handles add-to-cart, quantity updates, cart drawer, and mini-cart
 */

class ShopifyCart {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.drawerBody = document.querySelector('[data-cart-drawer-body]');
    this.drawerItems = document.querySelector('[data-cart-items]');
    this.cartEmpty = document.querySelector('[data-cart-empty]');
    this.cartFooter = document.querySelector('[data-cart-footer]');
    this.cartCount = document.querySelectorAll('[data-cart-count]');
    this.cartSubtotal = document.querySelectorAll('[data-cart-subtotal]');
    
    this.config = this.getConfig();
    
    this.init();
  }

  init() {
    // Bind close handlers
    const closeButtons = document.querySelectorAll('[data-cart-drawer-close]');
    closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.closeDrawer());
    });

    // Bind AJAX add-to-cart buttons
    const ajaxButtons = document.querySelectorAll('[data-ajax-add-to-cart]');
    ajaxButtons.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleAjaxAddToCart(e));
    });

    // Bind quick add buttons
    const quickAddButtons = document.querySelectorAll('[data-product-id][data-variant-id]');
    quickAddButtons.forEach(btn => {
      if (!btn.hasAttribute('data-ajax-add-to-cart')) {
        btn.addEventListener('click', (e) => this.handleQuickAdd(e));
      }
    });

    // Initialize product form variant selection
    this.initVariantSelection();

    // Update cart count on load
    this.updateCartCount();
  }

  getConfig() {
    const configElement = document.getElementById('cart-drawer-config');
    if (configElement) {
      try {
        return JSON.parse(configElement.textContent);
      } catch (e) {
        console.error('Error parsing cart config:', e);
      }
    }
    return {
      freeShippingThreshold: 5000,
      freeShippingMessage: 'You qualify for free shipping!',
      shippingProgressMessage: 'You\'re {amount} away from free shipping'
    };
  }

  initVariantSelection() {
    document.querySelectorAll('product-form').forEach(form => {
      const selects = form.querySelectorAll('[data-option-index]');
      const variantInput = form.querySelector('[data-variant-id]');
      
      if (selects.length > 0 && variantInput) {
        selects.forEach(select => {
          select.addEventListener('change', () => {
            // Variant selection is handled by the product-form web component
            // This is just for additional handling if needed
          });
        });
      }
    });
  }

  async handleAjaxAddToCart(e) {
    e.preventDefault();
    const button = e.currentTarget;
    const form = button.closest('form');
    
    if (!form) return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity')) || 1;

    if (!variantId) {
      this.showError('Please select a variant');
      return;
    }

    this.setButtonLoading(button, true);

    try {
      const result = await this.addToCart(variantId, quantity);
      this.setButtonLoading(button, false);
      if (result) {
        await this.updateDrawer();
        this.openDrawer();
        this.showToast('Added to cart!');
      }
    } catch (error) {
      this.setButtonLoading(button, false);
      this.showError(error.message || 'Could not add to cart');
    }
  }

  async handleQuickAdd(e) {
    e.preventDefault();
    const button = e.currentTarget;
    const variantId = button.dataset.variantId;
    const quantity = 1;

    if (!variantId) return;

    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.disabled = true;

    try {
      const result = await this.addToCart(variantId, quantity);
      
      if (result) {
        await this.updateDrawer();
        this.openDrawer();
        button.textContent = 'Added!';
    
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1000);
      }
    } catch (error) {
      button.textContent = originalText;
      button.disabled = false;
      this.showError(error.message || 'Could not add to cart');
    }
  }

  async addToCart(variantId, quantity = 1) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: quantity
        }]
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || error.message || 'Failed to add to cart');
    }

    return await response.json();
  }

  async getCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }
    return await response.json();
  }

  async changeQuantity(line, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        line: line,
        quantity: quantity
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update quantity');
    }

    return await response.json();
  }

  async updateDrawer() {
    try {
      const cart = await this.getCart();
      this.renderDrawer(cart);
      this.updateCartCount(cart.item_count);
    } catch (error) {
      console.error('Error updating drawer:', error);
    }
  }

  renderDrawer(cart) {
    if (!this.drawerItems) return;

    // Update count
    this.cartCount.forEach(el => {
      el.textContent = `(${cart.item_count})`;
    });

    // Update subtotal
    this.cartSubtotal.forEach(el => {
      el.textContent = this.formatMoney(cart.total_price);
    });

    // Show/hide empty state
    if (cart.item_count === 0) {
      if (this.cartEmpty) this.cartEmpty.style.display = 'block';
      this.drawerItems.innerHTML = '';
      if (this.cartFooter) this.cartFooter.style.display = 'none';
      return;
    }

    if (this.cartEmpty) this.cartEmpty.style.display = 'none';
    if (this.cartFooter) this.cartFooter.style.display = 'block';

    // Render items
    this.drawerItems.innerHTML = cart.items.map((item, index) => {
      return this.renderCartItem(item, index + 1);
    }).join('');

    // Bind quantity controls
    this.bindDrawerControls();

    // Update shipping progress
    this.updateShippingProgress(cart.total_price);
  }

  renderCartItem(item, line) {
    const variantTitle = item.variant_title !== 'Default Title' ? item.variant_title : '';
    
    return `
      <div class="cart-drawer-item" data-line="${line}" data-testid="drawer-item-${item.id}">
        <div class="drawer-item-image">
          <img src="${item.featured_image?.url || item.image}" alt="${item.title}" loading="lazy">
        </div>
        <div class="drawer-item-details">
          <div class="drawer-item-title">${item.product_title}</div>
          ${variantTitle ? `<div class="drawer-item-variant">${variantTitle}</div>` : ''}
          <div class="drawer-item-price">${this.formatMoney(item.price)}</div>
          <div class="drawer-item-quantity">
            <button class="drawer-qty-btn" data-qty-decrease="${line}" aria-label="Decrease quantity" data-testid="drawer-btn-decrease-${item.id}">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <line x1="3" y1="7" x2="11" y2="7" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
            <span class="drawer-qty-value" data-testid="drawer-qty-value-${item.id}">${item.quantity}</span>
            <button class="drawer-qty-btn" data-qty-increase="${line}" aria-label="Increase quantity" data-testid="drawer-btn-increase-${item.id}">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <line x1="7" y1="3" x2="7" y2="11" stroke="currentColor" stroke-width="2"/>
                <line x1="3" y1="7" x2="11" y2="7" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="drawer-item-remove">
          <div class="drawer-item-total" data-testid="drawer-item-total-${item.id}">${this.formatMoney(item.line_price)}</div>
          <button class="drawer-btn-remove" data-remove="${line}" aria-label="Remove item" data-testid="drawer-btn-remove-${item.id}">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="4" y1="4" x2="12" y2="12"></line>
              <line x1="12" y1="4" x2="4" y2="12"></line>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  bindDrawerControls() {
    // Decrease quantity
    document.querySelectorAll('[data-qty-decrease]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const line = parseInt(btn.dataset.qtyDecrease);
        const currentQty = parseInt(btn.parentElement.querySelector('.drawer-qty-value').textContent);
        if (currentQty > 1) {
          await this.changeQuantity(line, currentQty - 1);
          await this.updateDrawer();
        } else {
          //if (confirm('Remove this item from your cart?')) {
            await this.changeQuantity(line, 0);
            await this.updateDrawer();
          //}
        }
      });
    });

    // Increase quantity
    document.querySelectorAll('[data-qty-increase]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const line = parseInt(btn.dataset.qtyIncrease);
        const currentQty = parseInt(btn.parentElement.querySelector('.drawer-qty-value').textContent);
        await this.changeQuantity(line, currentQty + 1);
        await this.updateDrawer();
      });
    });

    // Remove item
    document.querySelectorAll('[data-remove]').forEach(btn => {
      btn.addEventListener('click', async () => {
        //if (confirm('Remove this item from your cart?')) {
          const line = parseInt(btn.dataset.remove);
          await this.changeQuantity(line, 0);
          await this.updateDrawer();
        //}
      });
    });
  }

  updateShippingProgress(totalPrice) {
    const progressBar = document.querySelector('[data-shipping-bar-fill]');
    const progressMessage = document.querySelector('[data-shipping-message]');
    
    if (!progressBar || !progressMessage) return;

    const threshold = this.config.freeShippingThreshold;
    const remaining = threshold - totalPrice;

    if (remaining <= 0) {
      // Free shipping achieved
      progressBar.style.width = '100%';
      progressMessage.textContent = this.config.freeShippingMessage;
      progressMessage.style.color = '#10b981';
    } else {
      // Show progress
      const percentage = (totalPrice / threshold) * 100;
      progressBar.style.width = `${percentage}%`;
      const remainingFormatted = this.formatMoney(remaining);
      progressMessage.textContent = this.config.shippingProgressMessage.replace('{amount}', remainingFormatted);
      progressMessage.style.color = '';
    }
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.style.display = 'block';
    document.body.style.overflow = 'hidden';
    
    // Trap focus in drawer
    const focusableElements = this.drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusableElements.length > 0) {
      setTimeout(() => focusableElements[0].focus(), 100);
    }
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.style.display = 'none';
    document.body.style.overflow = '';
  }

  updateCartCount(count) {
    if (count === undefined) {
      // Fetch current count
      this.getCart().then(cart => {
        this.cartCount.forEach(el => {
          el.textContent = `(${cart.item_count})`;
        });
      });
    } else {
      this.cartCount.forEach(el => {
        el.textContent = `(${count})`;
      });
    }
  }

  setButtonLoading(button, isLoading) {
    const btnText = button.querySelector('.btn-text');
    const btnLoading = button.querySelector('.btn-loading');

    if (btnText && btnLoading) {
      if (isLoading) {
        btnText.style.display = 'none';
        btnLoading.style.display = 'inline-flex';
        button.disabled = true;
      } else {
        btnText.style.display = 'inline';
        btnLoading.style.display = 'none';
        button.disabled = false;
      }
    } else {
      button.disabled = isLoading;
    }
  }

  showToast(message) {
    // Simple toast notification
    const toast = document.createElement('div');
    toast.className = 'cart-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 2rem;
      right: 2rem;
      background: #10b981;
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      animation: slideInUp 0.3s ease;
    `;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.animation = 'slideOutDown 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }

  showError(message) {
    const errorDiv = document.querySelector('[data-form-error]');
    if (errorDiv) {
      errorDiv.textContent = message;
      errorDiv.style.display = 'block';
      setTimeout(() => {
        errorDiv.style.display = 'none';
      }, 5000);
    } else {
      alert(message);
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
}

// Initialize cart on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.shopifyCart = new ShopifyCart();
  });
} else {
  window.shopifyCart = new ShopifyCart();
}

// Add CSS for toast animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideInUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes slideOutDown {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(100%);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);
